/* eslint-disable quotes */
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { Fragment, useState } from "react"
import { connect } from "react-redux"

import { fetchStylecode } from "areas/product/store/actions"
import {
  ChoiceOption,
  OptionTitle,
  OptionPrice,
  OptionsLabel,
  OptionsSlider,
  OptionImage,
} from "areas/product/wizard-options/components"
import { to_currency } from "helpers/number"
import { imageLookup } from "helpers/product/image-lookup"
import { filterProductOptions } from "helpers/product/product-filtering"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

const ChoiceOptions = ({
  options,
  pref,
  data,
  query,
  setChoice,
  selectedChoice,
  setSelectedChoice,
  filters,
}) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  // Example for data.image_size: JWAT01-32-BKBK-BRN Color (o2)
  let flexBasis = "28%"
  let tabletFlexBasis = "100px"
  if (data.image_size) {
    try {
      const parsedImageSize = JSON.parse(data.image_size)
      flexBasis = parsedImageSize._
      tabletFlexBasis = parsedImageSize.tablet
    } catch (err) {
      flexBasis = tabletFlexBasis = data.image_size
    }
  }

  return (
    <OptionsSlider
      aria-label={`${data.heading.title} Options`}
      role="radiogroup"
      style={{
        "--flex-basis": flexBasis,
        "--tablet-flex-basis": tabletFlexBasis,
      }}
    >
      {filterProductOptions(options, filters, true).map((opt, i) => {
        const selected = selectedChoice === opt.style_code
        return (
          <ChoiceOption
            aria-checked={selected}
            className="tablet:basis-(--tablet-flex-basis) basis-(--flex-basis)"
            data-cy={`product-wizard-option-${pref}-${opt.value}`}
            key={`switch-${opt.style_code}`}
            onClick={() => {
              setSelectedChoice(opt.style_code)
              setChoice(opt)
            }}
            role="radio"
            tabIndex={selected ? 0 : -1}
            {...keyboardProps}
          >
            <OptionImage
              alt={opt.display_name}
              className={cn("h-auto", data.image_size ? "w-full" : "w-70")}
              lazyload={false}
              src={
                opt.thumb
                  ? imageLookup("choice", opt.thumb, "simple")
                  : `${window.sl.config.full_main_render_url}/${opt.style_code}${query}`
              }
            />
            <OptionTitle>
              {opt.display_name.split("\n").map((line, i2) => (
                <Fragment key={`switcherlabel${i}${i2}`}>
                  {line}
                  <br />
                </Fragment>
              ))}
            </OptionTitle>
            <OptionPrice>
              {(opt.display_price || opt.price) > 0
                ? to_currency(opt.display_price || opt.price)
                : ""}
            </OptionPrice>
          </ChoiceOption>
        )
      })}
    </OptionsSlider>
  )
}
ChoiceOptions.propTypes = {
  data: PropTypes.object,
  filters: PropTypes.object,
  options: PropTypes.array,
  pref: PropTypes.string,
  query: PropTypes.string,
  selectedChoice: PropTypes.string,
  setChoice: PropTypes.func,
  setSelectedChoice: PropTypes.func,
}

/**
 * Switch stylecode without refreshing page
 * sample product: JWLN0473-4
 */
const InternalStylecodeSwitcher = ({
  data,
  displayLabel,
  filters,
  options,
  pref,
  query,
  selectedValue,
  setChoice,
}) => {
  const [selectedChoice, setSelectedChoice] = useState(selectedValue)

  return (
    <div>
      {displayLabel && (
        <OptionsLabel className="mb-8">{displayLabel}:</OptionsLabel>
      )}
      <FocusScope>
        <ChoiceOptions
          data={data}
          filters={filters}
          options={options}
          pref={pref}
          query={query}
          selectedChoice={selectedChoice}
          setChoice={setChoice}
          setSelectedChoice={setSelectedChoice}
        />
      </FocusScope>
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  let query = "?dim=100"
  Object.keys(state.choices)
    .filter((key) => !state.choices[key].disabled)
    .forEach((key) => {
      const choice = state.choices[key]
      query += `&${choice.pref}=${encodeURIComponent(choice.value)}`
    })

  return {
    filters: state.choices,
    query,
    selectedValue:
      state.choices[ownProps.pref] && state.choices[ownProps.pref].style_code,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    setChoice: (data) => {
      dispatch(fetchStylecode(data.style_code))
    },
  }
}

InternalStylecodeSwitcher.propTypes = {
  data: PropTypes.object,
  displayLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  filters: PropTypes.object,
  options: PropTypes.array,
  pref: PropTypes.string,
  query: PropTypes.string,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
}

const StylecodeSwitcher = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalStylecodeSwitcher)

export default StylecodeSwitcher
